import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { FormMsg, NoFormMsg, doFormMsg } from 'components/forms/FormMessage';
import { ModalDialog } from 'components/modal/Modal';
import './AdminMaintenanceMode.scss';

type MaintenanceModeResponse = {
  maintenanceMode: boolean;
};

export default function AdminMaintenanceMode() {
  const intl = useIntl();
  const [maintenanceMode, setMaintenanceMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  const [token] = useState<string | undefined>(Cookies.get('token'));

  // Fetch initial maintenance mode status
  useEffect(() => {
    fetchMaintenanceStatus();
  }, []);

  async function fetchMaintenanceStatus() {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND}admin/maintenance`,
      );
      if (response.ok) {
        const data: MaintenanceModeResponse = await response.json();
        setMaintenanceMode(data.maintenanceMode);
        setFormMsg(NoFormMsg);
      } else {
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
            success: false,
          },
          setFormMsg,
        );
      }
    } catch (error) {
      console.error('Error fetching maintenance status:', error);
      doFormMsg(
        {
          message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
          success: false,
        },
        setFormMsg,
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!token) {
          doFormMsg(
            {
              message: intl.formatMessage({
                id: 'ADMIN.MAINTENANCE_MODE.AUTH_REQUIRED',
              }),
              success: false,
            },
            setFormMsg,
          );
          reject(
            intl.formatMessage({
              id: 'ADMIN.MAINTENANCE_MODE.AUTH_REQUIRED',
            }),
          );
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND}admin/maintenance`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ maintenanceMode }),
          },
        );

        if (response.ok) {
          const data: MaintenanceModeResponse = await response.json();
          setMaintenanceMode(data.maintenanceMode);
          doFormMsg(
            {
              message: intl.formatMessage({ id: 'X.SAVED' }),
              success: true,
            },
            setFormMsg,
          );
          // Reload page immediately after successful save to reflect maintenance mode change
          // This ensures the change takes effect whether form is accessed as a page or modal
          window.location.reload();
          resolve(true);
        } else if (response.status === 401) {
          doFormMsg(
            {
              message: intl.formatMessage({
                id: 'ADMIN.MAINTENANCE_MODE.UNAUTHORIZED',
              }),
              success: false,
            },
            setFormMsg,
          );
          reject(
            intl.formatMessage({
              id: 'ADMIN.MAINTENANCE_MODE.UNAUTHORIZED',
            }),
          );
        } else if (response.status === 403) {
          doFormMsg(
            {
              message: intl.formatMessage({
                id: 'ADMIN.MAINTENANCE_MODE.FORBIDDEN',
              }),
              success: false,
            },
            setFormMsg,
          );
          reject(
            intl.formatMessage({
              id: 'ADMIN.MAINTENANCE_MODE.FORBIDDEN',
            }),
          );
        } else {
          doFormMsg(
            {
              message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
              success: false,
            },
            setFormMsg,
          );
          reject(intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }));
        }
      } catch (error) {
        console.error('Error updating maintenance mode:', error);
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
            success: false,
          },
          setFormMsg,
        );
        reject(intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }));
      }
    });
  }

  if (loading) {
    return (
      <ModalDialog
        className="admin-maintenance-mode"
        title={<FormattedMessage id="ADMIN.MAINTENANCE_MODE.TITLE" />}
        okBtn={{ disabled: true }}
        cancelBtn={{ disabled: true }}
      >
        <div className="text-center p-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </ModalDialog>
    );
  }

  return (
    <ModalDialog
      className="admin-maintenance-mode"
      title={<FormattedMessage id="ADMIN.MAINTENANCE_MODE.TITLE" />}
      okBtn={{
        onClick: handleSubmit,
        label: intl.formatMessage({ id: 'X.SAVE' }),
      }}
      formMsg={formMsg}
    >
      <Form.Group as={Row}>
        <Col sm={12}>
          <Form.Group className="mb-4">
            <Form.Label>
              <FormattedMessage id="ADMIN.MAINTENANCE_MODE.STATUS" />
            </Form.Label>
            <div className="status-display p-3 bg-light rounded border">
              <strong>
                {maintenanceMode ? (
                  <span className="text-danger">
                    <FormattedMessage id="ADMIN.MAINTENANCE_MODE.ENABLED" />
                  </span>
                ) : (
                  <span className="text-success">
                    <FormattedMessage id="ADMIN.MAINTENANCE_MODE.DISABLED" />
                  </span>
                )}
              </strong>
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Check
              type="switch"
              id="maintenance-toggle"
              label={
                <FormattedMessage id="ADMIN.MAINTENANCE_MODE.TOGGLE_LABEL" />
              }
              checked={maintenanceMode}
              onChange={(e) => setMaintenanceMode(e.target.checked)}
            />
          </Form.Group>

          <div className="p-3 bg-info bg-opacity-10 rounded">
            <p className="small mb-0">
              <FormattedMessage id="ADMIN.MAINTENANCE_MODE.INFO" />
            </p>
          </div>
        </Col>
      </Form.Group>
    </ModalDialog>
  );
}
